import { readDocxXmlParts } from './docx-zip-reader';
import { ImageDimension, parseDocxXmlModel, PageGeometry, ParagraphSpacing, TableFidelity } from './docx-xml-model';
import {
  buildListFormatCss,
  buildListFormatStyleMapOverrides,
  createFidelityTransformDocument,
  LAST_RENDERED_PAGE_BREAK_STYLE_MAP_RULE,
} from './docx-fidelity-transform';

// Page splitting
export const DOCX_STYLE_MAP = [
  "br[type='page'] => hr.page-break",
  "u => u",
  "r[style-name='Definition'] => span.term-definition",
];

const PAGE_BREAK_SELECTOR = 'hr.page-break';

function cloneShallow(doc: Document, el: Element): Element {
  const clone = doc.createElement(el.tagName);
  Array.from(el.attributes).forEach((attr) => clone.setAttribute(attr.name, attr.value));
  return clone;
}

function collectFollowingSiblings(node: Node): Node[] {
  const result: Node[] = [];
  let sibling = node.nextSibling;
  while (sibling) {
    result.push(sibling);
    sibling = sibling.nextSibling;
  }
  return result;
}

function splitContainerAtMarker(root: Element, marker: Element): Element {
  const doc = root.ownerDocument;
  const markerParent = marker.parentNode as Element;

  const innerFollowing = collectFollowingSiblings(marker);
  marker.remove();
  let afterLevel: Element = cloneShallow(doc, markerParent);
  innerFollowing.forEach((node) => afterLevel.appendChild(node));

  let current: Element = markerParent;
  while (current !== root) {
    const parent = current.parentNode as Element;
    const following = collectFollowingSiblings(current);
    const parentClone = cloneShallow(doc, parent);
    parentClone.appendChild(afterLevel);
    following.forEach((node) => parentClone.appendChild(node));
    afterLevel = parentClone;
    current = parent;
  }

  return afterLevel;
}

export function splitIntoPages(html: string): string[] {
  const doc = new DOMParser().parseFromString(`<div id="docx-split-root">${html}</div>`, 'text/html');
  let root = doc.getElementById('docx-split-root') as Element;

  const pages: string[] = [];
  let marker = root.querySelector(PAGE_BREAK_SELECTOR);

  while (marker) {
    const after = splitContainerAtMarker(root, marker);
    pages.push(root.innerHTML);
    root = after;
    marker = root.querySelector(PAGE_BREAK_SELECTOR);
  }
  pages.push(root.innerHTML);

  return pages;
}

// Run style resolution
const W_NS_STYLE = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

export interface ResolvedRunFormatting {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
}

function styleLocalName(el: Element): string {
  return el.localName || el.tagName.replace(/^.*:/, '');
}

function styleGetAttr(el: Element, name: string): string | null {
  return el.getAttributeNS(W_NS_STYLE, name) || el.getAttribute(`w:${name}`) || el.getAttribute(name);
}

function styleChildren(el: Element, name: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < el.childNodes.length; i++) {
    const node = el.childNodes[i];
    if (node.nodeType === 1 && styleLocalName(node as Element) === name) {
      result.push(node as Element);
    }
  }
  return result;
}

function styleFirstChild(el: Element, name: string): Element | null {
  const found = styleChildren(el, name);
  return found.length ? found[0] : null;
}

function isToggleOn(el: Element | null): boolean {
  if (!el) {
    return false;
  }
  const val = styleGetAttr(el, 'val');
  return val !== 'false' && val !== '0';
}

interface RawStyle {
  basedOn: string | null;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
}

export function resolveRunFormattingByStyleId(stylesXml: string | null): Record<string, ResolvedRunFormatting> {
  const resolved: Record<string, ResolvedRunFormatting> = {};
  if (!stylesXml) {
    return resolved;
  }

  const doc = new DOMParser().parseFromString(stylesXml, 'application/xml');
  const root = doc.documentElement;

  const rawStyles: Record<string, RawStyle> = {};

  styleChildren(root, 'style').forEach((styleEl) => {
    const styleId = styleGetAttr(styleEl, 'styleId');
    if (!styleId) {
      return;
    }
    const rPr = styleFirstChild(styleEl, 'rPr');
    const basedOnEl = styleFirstChild(styleEl, 'basedOn');
    rawStyles[styleId] = {
      basedOn: basedOnEl ? styleGetAttr(basedOnEl, 'val') : null,
      isBold: !!rPr && isToggleOn(styleFirstChild(rPr, 'b')),
      isItalic: !!rPr && isToggleOn(styleFirstChild(rPr, 'i')),
      isUnderline: !!rPr && !!styleFirstChild(rPr, 'u') && styleGetAttr(styleFirstChild(rPr, 'u') as Element, 'val') !== 'none',
    };
  });

  function resolve(styleId: string, seen: Set<string>): ResolvedRunFormatting {
    if (resolved[styleId]) {
      return resolved[styleId];
    }
    if (seen.has(styleId)) {
      return { isBold: false, isItalic: false, isUnderline: false };
    }
    seen.add(styleId);

    const raw = rawStyles[styleId];
    if (!raw) {
      return { isBold: false, isItalic: false, isUnderline: false };
    }

    const base = raw.basedOn ? resolve(raw.basedOn, seen) : { isBold: false, isItalic: false, isUnderline: false };
    const result: ResolvedRunFormatting = {
      isBold: raw.isBold || base.isBold,
      isItalic: raw.isItalic || base.isItalic,
      isUnderline: raw.isUnderline || base.isUnderline,
    };
    resolved[styleId] = result;
    return result;
  }

  Object.keys(rawStyles).forEach((styleId) => resolve(styleId, new Set()));
  return resolved;
}

export interface ResolvedRunFont {
  fontFamily: string | null;
  fontSizePt: number | null;
}

interface RawFont {
  basedOn: string | null;
  fontFamily: string | null;
  fontSizePt: number | null;
}

function readRunFontFromRPr(rPr: Element | null): { fontFamily: string | null; fontSizePt: number | null } {
  if (!rPr) {
    return { fontFamily: null, fontSizePt: null };
  }
  const rFonts = styleFirstChild(rPr, 'rFonts');
  const fontFamily = rFonts ? styleGetAttr(rFonts, 'ascii') || styleGetAttr(rFonts, 'hAnsi') : null;
  const szEl = styleFirstChild(rPr, 'sz');
  const szVal = szEl ? styleGetAttr(szEl, 'val') : null;
  const fontSizePt = szVal && /^[0-9]+$/.test(szVal) ? parseInt(szVal, 10) / 2 : null;
  return { fontFamily, fontSizePt };
}

// Word resolves a run's effective font/size by cascading: direct run formatting (already
// handled by mammoth's own w:rPr reading) > character style (rStyle) > the ENCLOSING
// PARAGRAPH's style (a paragraph style's own rPr supplies default run formatting for every
// run inside it) > docDefaults. mammoth only reads a run's own direct w:sz/w:rFonts and
// never climbs this chain, so a run relying on its paragraph style for font/size (very
// common for table-cell "header" text styled e.g. "Heading1" but locally redefined to a
// small size) silently falls back to the browser's default tag size instead. This resolves
// the SAME chain mammoth is missing, keyed by styleId, for both paragraph and character
// styles - the fidelity transform decides at the run level whether a paragraph or character
// styleId applies (see docx-fidelity-transform.ts).
export function resolveRunFontByStyleId(stylesXml: string | null): {
  byStyleId: Record<string, ResolvedRunFont>;
  docDefault: ResolvedRunFont;
} {
  const docDefault: ResolvedRunFont = { fontFamily: null, fontSizePt: null };
  const byStyleId: Record<string, ResolvedRunFont> = {};
  if (!stylesXml) {
    return { byStyleId, docDefault };
  }

  const doc = new DOMParser().parseFromString(stylesXml, 'application/xml');
  const root = doc.documentElement;

  const docDefaultsEl = styleFirstChild(root, 'docDefaults');
  if (docDefaultsEl) {
    const rPrDefaultEl = styleFirstChild(docDefaultsEl, 'rPrDefault');
    const rPr = rPrDefaultEl ? styleFirstChild(rPrDefaultEl, 'rPr') : null;
    const font = readRunFontFromRPr(rPr);
    docDefault.fontFamily = font.fontFamily;
    docDefault.fontSizePt = font.fontSizePt;
  }

  const rawByStyleId: Record<string, RawFont> = {};
  styleChildren(root, 'style').forEach((styleEl) => {
    const styleId = styleGetAttr(styleEl, 'styleId');
    if (!styleId) {
      return;
    }
    const basedOnEl = styleFirstChild(styleEl, 'basedOn');
    const font = readRunFontFromRPr(styleFirstChild(styleEl, 'rPr'));
    rawByStyleId[styleId] = {
      basedOn: basedOnEl ? styleGetAttr(basedOnEl, 'val') : null,
      fontFamily: font.fontFamily,
      fontSizePt: font.fontSizePt,
    };
  });

  function resolve(styleId: string, seen: Set<string>): ResolvedRunFont {
    if (byStyleId[styleId]) {
      return byStyleId[styleId];
    }
    if (seen.has(styleId)) {
      return { ...docDefault };
    }
    seen.add(styleId);

    const raw = rawByStyleId[styleId];
    if (!raw) {
      return { ...docDefault };
    }

    const base = raw.basedOn ? resolve(raw.basedOn, seen) : docDefault;
    const result: ResolvedRunFont = {
      fontFamily: raw.fontFamily ?? base.fontFamily,
      fontSizePt: raw.fontSizePt ?? base.fontSizePt,
    };
    byStyleId[styleId] = result;
    return result;
  }

  Object.keys(rawByStyleId).forEach((styleId) => resolve(styleId, new Set()));
  return { byStyleId, docDefault };
}

// Numbering format resolution
import { ParagraphNumberingRef } from './docx-xml-model';

const W_NS_NUM = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

export type DocxNumberFormat =
  | 'decimal'
  | 'lowerLetter'
  | 'upperLetter'
  | 'lowerRoman'
  | 'upperRoman'
  | 'bullet'
  | 'other';

export interface NumberingLevelDetail {
  lvlText: string;
  start: number;
  numFmt: DocxNumberFormat;
  indentLeftTwips: number;
  hangingTwips: number;
}

export const CSS_LIST_STYLE_TYPE: Record<DocxNumberFormat, string> = {
  decimal: 'decimal',
  lowerLetter: 'lower-alpha',
  upperLetter: 'upper-alpha',
  lowerRoman: 'lower-roman',
  upperRoman: 'upper-roman',
  bullet: 'disc',
  other: 'decimal',
};

function numLocalName(el: Element): string {
  return el.localName || el.tagName.replace(/^.*:/, '');
}

function numGetAttr(el: Element, name: string): string | null {
  return el.getAttributeNS(W_NS_NUM, name) || el.getAttribute(`w:${name}`) || el.getAttribute(name);
}

function numChildren(el: Element, name: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < el.childNodes.length; i++) {
    const node = el.childNodes[i];
    if (node.nodeType === 1 && numLocalName(node as Element) === name) {
      result.push(node as Element);
    }
  }
  return result;
}

function numFirstChild(el: Element, name: string): Element | null {
  const found = numChildren(el, name);
  return found.length ? found[0] : null;
}

function normalizeFormat(numFmt: string | null): DocxNumberFormat {
  switch (numFmt) {
    case 'decimal':
    case 'decimalZero':
      return 'decimal';
    case 'lowerLetter':
      return 'lowerLetter';
    case 'upperLetter':
      return 'upperLetter';
    case 'lowerRoman':
      return 'lowerRoman';
    case 'upperRoman':
      return 'upperRoman';
    case 'bullet':
      return 'bullet';
    default:
      return 'other';
  }
}

interface AbstractNumLevels {
  [ilvl: string]: DocxNumberFormat;
}

// Formats whose exact text can be pre-computed and injected as a label run
// (see computeParagraphNumberLabels). Bullet has no counter to render, and
// "other"/unrecognised formats fall back to mammoth's default <ol>/<li>.
const ORDERED_LABEL_FORMATS = new Set<DocxNumberFormat>([
  'decimal',
  'lowerLetter',
  'upperLetter',
  'lowerRoman',
  'upperRoman',
]);

function toRomanNumeral(value: number): string {
  if (value <= 0) {
    return '';
  }
  const numerals: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let remaining = value;
  let result = '';
  for (const [amount, symbol] of numerals) {
    while (remaining >= amount) {
      result += symbol;
      remaining -= amount;
    }
  }
  return result;
}

// Word's lowerLetter/upperLetter sequence repeats the alphabet rather than
// carrying like a base-26 number: after "z" comes "aa", "bb", ... "zz", "aaa".
function toLetterSequence(value: number): string {
  if (value <= 0) {
    return '';
  }
  const letter = String.fromCharCode(97 + ((value - 1) % 26));
  const repeatCount = Math.floor((value - 1) / 26) + 1;
  return letter.repeat(repeatCount);
}

function formatCounterValue(value: number, numFmt: DocxNumberFormat): string {
  switch (numFmt) {
    case 'lowerLetter':
      return toLetterSequence(value);
    case 'upperLetter':
      return toLetterSequence(value).toUpperCase();
    case 'lowerRoman':
      return toRomanNumeral(value).toLowerCase();
    case 'upperRoman':
      return toRomanNumeral(value).toUpperCase();
    default:
      return String(value);
  }
}

export function resolveDominantFormatsPerLevel(
  numberingXml: string | null,
  paragraphNumbering: Array<ParagraphNumberingRef | null>
): Record<number, DocxNumberFormat> {
  const result: Record<number, DocxNumberFormat> = {};
  if (!numberingXml) {
    return result;
  }

  const doc = new DOMParser().parseFromString(numberingXml, 'application/xml');
  const root = doc.documentElement;

  const abstractNumIdByNumId: Record<string, string> = {};
  numChildren(root, 'num').forEach((numEl) => {
    const numId = numGetAttr(numEl, 'numId');
    const abstractNumIdEl = numFirstChild(numEl, 'abstractNumId');
    const abstractNumId = abstractNumIdEl ? numGetAttr(abstractNumIdEl, 'val') : null;
    if (numId && abstractNumId) {
      abstractNumIdByNumId[numId] = abstractNumId;
    }
  });

  const levelsByAbstractNumId: Record<string, AbstractNumLevels> = {};
  numChildren(root, 'abstractNum').forEach((abstractNumEl) => {
    const abstractNumId = numGetAttr(abstractNumEl, 'abstractNumId');
    if (!abstractNumId) {
      return;
    }
    const levels: AbstractNumLevels = {};
    numChildren(abstractNumEl, 'lvl').forEach((lvlEl) => {
      const ilvl = numGetAttr(lvlEl, 'ilvl');
      const numFmtEl = numFirstChild(lvlEl, 'numFmt');
      const numFmt = numFmtEl ? numGetAttr(numFmtEl, 'val') : null;
      if (ilvl !== null) {
        levels[ilvl] = normalizeFormat(numFmt);
      }
    });
    levelsByAbstractNumId[abstractNumId] = levels;
  });

  function formatFor(numId: string, ilvl: number): DocxNumberFormat | null {
    const abstractNumId = abstractNumIdByNumId[numId];
    if (abstractNumId === undefined) {
      return null;
    }
    const levels = levelsByAbstractNumId[abstractNumId];
    if (!levels) {
      return null;
    }
    return levels[String(ilvl)] || null;
  }

  const countsByLevel: Record<number, Record<string, number>> = {};
  paragraphNumbering.forEach((ref) => {
    if (!ref) {
      return;
    }
    const format = formatFor(ref.numId, ref.ilvl);
    if (!format) {
      return;
    }
    countsByLevel[ref.ilvl] = countsByLevel[ref.ilvl] || {};
    countsByLevel[ref.ilvl][format] = (countsByLevel[ref.ilvl][format] || 0) + 1;
  });

  Object.keys(countsByLevel).forEach((levelKey) => {
    const level = parseInt(levelKey, 10);
    const counts = countsByLevel[level];
    const dominant = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] as DocxNumberFormat;
    result[level] = dominant;
  });

  return result;
}

export function resolveNumberingLevelDetails(numberingXml: string | null): Record<string, NumberingLevelDetail> {
  const result: Record<string, NumberingLevelDetail> = {};
  if (!numberingXml) {
    return result;
  }

  const doc = new DOMParser().parseFromString(numberingXml, 'application/xml');
  const root = doc.documentElement;

  // Bail out gracefully on XML parse errors
  if (root.nodeName === 'parsererror' || numChildren(root, 'abstractNum').length === 0 && numChildren(root, 'num').length === 0) {
    const parseError = root.getElementsByTagName('parsererror')[0];
    if (parseError) {
      return result;
    }
  }

  const levelsByAbstractNumId: Record<string, Record<string, NumberingLevelDetail>> = {};
  numChildren(root, 'abstractNum').forEach((abstractNumEl) => {
    const abstractNumId = numGetAttr(abstractNumEl, 'abstractNumId');
    if (!abstractNumId) {
      return;
    }
    const levels: Record<string, NumberingLevelDetail> = {};
    numChildren(abstractNumEl, 'lvl').forEach((lvlEl) => {
      const ilvl = numGetAttr(lvlEl, 'ilvl');
      if (ilvl === null) {
        return;
      }
      const lvlTextEl = numFirstChild(lvlEl, 'lvlText');
      const startEl = numFirstChild(lvlEl, 'start');
      const numFmtEl = numFirstChild(lvlEl, 'numFmt');
      const indEl = numFirstChild(lvlEl, 'ind');

      const lvlText = lvlTextEl ? (numGetAttr(lvlTextEl, 'val') ?? '') : '';
      const startVal = startEl ? parseInt(numGetAttr(startEl, 'val') ?? '1', 10) : 1;
      const numFmt = normalizeFormat(numFmtEl ? numGetAttr(numFmtEl, 'val') : null);
      let indentLeftTwips = 0;
      let hangingTwips = 0;
      if (indEl) {
        const leftAttr = numGetAttr(indEl, 'left') ?? numGetAttr(indEl, 'start');
        const hangingAttr = numGetAttr(indEl, 'hanging');
        indentLeftTwips = leftAttr ? parseInt(leftAttr, 10) : 0;
        hangingTwips = hangingAttr ? parseInt(hangingAttr, 10) : 0;
      }

      levels[ilvl] = {
        lvlText,
        start: Number.isFinite(startVal) ? startVal : 1,
        numFmt,
        indentLeftTwips: Number.isFinite(indentLeftTwips) ? indentLeftTwips : 0,
        hangingTwips: Number.isFinite(hangingTwips) ? hangingTwips : 0,
      };
    });
    levelsByAbstractNumId[abstractNumId] = levels;
  });

  // For each <w:num>, merge base abstractNum levels with any lvlOverride children
  numChildren(root, 'num').forEach((numEl) => {
    const numId = numGetAttr(numEl, 'numId');
    const abstractNumIdEl = numFirstChild(numEl, 'abstractNumId');
    const abstractNumId = abstractNumIdEl ? numGetAttr(abstractNumIdEl, 'val') : null;
    if (!numId || !abstractNumId) {
      return;
    }

    const baseLevels = levelsByAbstractNumId[abstractNumId] || {};

    const allIlvls = new Set<string>(Object.keys(baseLevels));

    numChildren(numEl, 'lvlOverride').forEach((overrideEl) => {
      const ilvl = numGetAttr(overrideEl, 'ilvl');
      if (ilvl !== null) {
        allIlvls.add(ilvl);
      }
    });

    allIlvls.forEach((ilvl) => {
      const base: NumberingLevelDetail = baseLevels[ilvl] || {
        lvlText: '',
        start: 1,
        numFmt: 'other' as DocxNumberFormat,
        indentLeftTwips: 0,
        hangingTwips: 0,
      };

      const overrideEl = numChildren(numEl, 'lvlOverride').find(
        (el) => numGetAttr(el, 'ilvl') === ilvl
      );

      if (overrideEl) {
        // Override can contain a <w:lvl> child with full level definition, or just <w:startOverride>
        const overrideLvlEl = numFirstChild(overrideEl, 'lvl');
        const startOverrideEl = numFirstChild(overrideEl, 'startOverride');

        let lvlText = base.lvlText;
        let start = base.start;
        let numFmt = base.numFmt;
        let indentLeftTwips = base.indentLeftTwips;
        let hangingTwips = base.hangingTwips;

        if (overrideLvlEl) {
          const lvlTextEl = numFirstChild(overrideLvlEl, 'lvlText');
          const startEl = numFirstChild(overrideLvlEl, 'start');
          const numFmtEl = numFirstChild(overrideLvlEl, 'numFmt');
          const indEl = numFirstChild(overrideLvlEl, 'ind');

          if (lvlTextEl) {
            lvlText = numGetAttr(lvlTextEl, 'val') ?? lvlText;
          }
          if (startEl) {
            const v = parseInt(numGetAttr(startEl, 'val') ?? '', 10);
            if (Number.isFinite(v)) {
              start = v;
            }
          }
          if (numFmtEl) {
            numFmt = normalizeFormat(numGetAttr(numFmtEl, 'val'));
          }
          if (indEl) {
            const leftAttr = numGetAttr(indEl, 'left') ?? numGetAttr(indEl, 'start');
            const hangingAttr = numGetAttr(indEl, 'hanging');
            if (leftAttr !== null) {
              const v = parseInt(leftAttr, 10);
              if (Number.isFinite(v)) {
                indentLeftTwips = v;
              }
            }
            if (hangingAttr !== null) {
              const v = parseInt(hangingAttr, 10);
              if (Number.isFinite(v)) {
                hangingTwips = v;
              }
            }
          }
        }

        if (startOverrideEl) {
          const v = parseInt(numGetAttr(startOverrideEl, 'val') ?? '', 10);
          if (Number.isFinite(v)) {
            start = v;
          }
        }

        result[`${numId}:${ilvl}`] = { lvlText, start, numFmt, indentLeftTwips, hangingTwips };
      } else {
        result[`${numId}:${ilvl}`] = { ...base };
      }
    });
  });

  return result;
}

export function computeParagraphNumberLabels(
  paragraphNumbering: Array<ParagraphNumberingRef | null>,
  levelDetails: Record<string, NumberingLevelDetail>
): (string | null)[] {
  const labels: (string | null)[] = [];
  // counterState: "numId:ilvl" -> current counter value (already incremented)
  const counterState: Record<string, number> = {};
  // lastCounterByIlvl: ilvl -> { numId, counter } for continuation detection
  const lastCounterByIlvl: Record<number, { numId: string; counter: number }> = {};

  for (const ref of paragraphNumbering) {
    if (!ref) {
      labels.push(null);
      continue;
    }

    const key = `${ref.numId}:${ref.ilvl}`;
    const detail = levelDetails[key];
    if (!detail || !ORDERED_LABEL_FORMATS.has(detail.numFmt)) {
      labels.push(null);
      continue;
    }

    // Initialise counter for this key if not present
    if (counterState[key] === undefined) {
      // Continuation detection: if a prior numId at same ilvl had counter C,
      // used the same numFmt, and this level's start === C + 1, continue from
      // C rather than start-1. Restricting to matching numFmt avoids treating
      // e.g. an unrelated lowerRoman list as a continuation of a lowerLetter one
      // just because their start values happen to line up numerically.
      const prior = lastCounterByIlvl[ref.ilvl];
      if (prior && prior.numId !== ref.numId) {
        const priorDetail = levelDetails[`${prior.numId}:${ref.ilvl}`];
        if (priorDetail && priorDetail.numFmt === detail.numFmt && detail.start === prior.counter + 1) {
          counterState[key] = prior.counter; // will be incremented to detail.start
        } else {
          counterState[key] = detail.start - 1;
        }
      } else {
        counterState[key] = detail.start - 1;
      }
    }

    // Reset child counters (ilvl > current) by deleting them from counterState
    // so they reinitialise on next occurrence.
    Object.keys(counterState).forEach((k) => {
      const colonIdx = k.lastIndexOf(':');
      const kNumId = k.substring(0, colonIdx);
      const kIlvl = parseInt(k.substring(colonIdx + 1), 10);
      if (kIlvl > ref.ilvl) {
        const kPrior = lastCounterByIlvl[kIlvl];
        if (kNumId === ref.numId || (kPrior && kPrior.numId === kNumId)) {
          delete counterState[k];
        }
      }
    });

    counterState[key]++;
    lastCounterByIlvl[ref.ilvl] = { numId: ref.numId, counter: counterState[key] };

    // Substitute %K placeholders in lvlText, rendering each referenced level's
    // counter using THAT level's own numFmt (a "%1.%2." template can mix a
    // decimal parent with a lowerLetter child, for example).
    const label = detail.lvlText.replace(/%(\d+)/g, (_match, kStr: string) => {
      const k = parseInt(kStr, 10) - 1; // %1 -> ilvl 0, %2 -> ilvl 1, etc.
      const refKey = `${ref.numId}:${k}`;
      const counterValue = counterState[refKey] ?? 0;
      const refFmt = levelDetails[refKey]?.numFmt ?? detail.numFmt;
      return formatCounterValue(counterValue, refFmt);
    });

    labels.push(label);
  }

  return labels;
}

// Table fidelity
function twipsToPercent(widthsTwips: number[], index: number): number | null {
  const total = widthsTwips.reduce((sum, w) => sum + w, 0);
  if (!total || widthsTwips[index] === undefined) {
    return null;
  }
  return Math.round((widthsTwips[index] / total) * 10000) / 100;
}

export function applyTableFidelity(html: string, tables: TableFidelity[]): string {
  if (tables.length === 0) {
    return html;
  }

  const doc = new DOMParser().parseFromString(`<div id="docx-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('docx-root');
  if (!root) {
    return html;
  }

  const tableElements = Array.from(root.querySelectorAll('table'));

  tableElements.forEach((tableEl, tableIndex) => {
    const fidelity = tables[tableIndex];
    if (!fidelity) {
      return;
    }

    if (fidelity.columnWidthsTwips.length > 0) {
      const total = fidelity.columnWidthsTwips.reduce((sum, w) => sum + w, 0);
      if (total > 0) {
        const colgroup = doc.createElement('colgroup');
        fidelity.columnWidthsTwips.forEach((_, colIndex) => {
          const percent = twipsToPercent(fidelity.columnWidthsTwips, colIndex);
          const col = doc.createElement('col');
          if (percent !== null) {
            col.setAttribute('style', `width: ${percent}%`);
          }
          colgroup.appendChild(col);
        });
        tableEl.insertBefore(colgroup, tableEl.firstChild);
        (tableEl as HTMLElement).style.width = '100%';
        (tableEl as HTMLElement).style.tableLayout = 'fixed';
      }
    }

    const rowElements = Array.from((tableEl as HTMLTableElement).rows);
    rowElements.forEach((rowEl, rowIndex) => {
      const rowFidelity = fidelity.rows[rowIndex];
      if (!rowFidelity) {
        return;
      }

      const cellElements = Array.from(rowEl.cells);
      cellElements.forEach((cellEl, cellIndex) => {
        const cellFidelity = rowFidelity[cellIndex];
        if (!cellFidelity) {
          return;
        }

        const declarations: string[] = [];
        (['top', 'right', 'bottom', 'left'] as const).forEach((side) => {
          const border = cellFidelity.borders[side];
          if (border) {
            declarations.push(`border-${side}: ${border}`);
          }
        });
        if (cellFidelity.shadingFill) {
          declarations.push(`background-color: #${cellFidelity.shadingFill}`);
        }

        if (declarations.length > 0) {
          const existing = cellEl.getAttribute('style');
          cellEl.setAttribute('style', existing ? `${existing}; ${declarations.join('; ')}` : declarations.join('; '));
        }
      });
    });
  });

  return root.innerHTML;
}

export interface ConversionContext {
  styleMap: string[];
  paragraphSpacing: ParagraphSpacing[];
  runFormattingByStyleId: Record<string, ResolvedRunFormatting>;
  runIsLastRenderedPageBreakMarker: boolean[];
  paragraphHasSectionPageBreak: boolean[];
  tables: TableFidelity[];
  listFormatCss: string;
  diagnostics: string[];
  pageGeometry: PageGeometry;
  explicitPageBreakCount: number;
  paragraphNumberLabel: (string | null)[];
  paragraphNumberIndent: Array<{ leftTwips: number; hangingTwips: number } | null>;
  imageDimensions: Array<ImageDimension | null>;
  runParagraphStyleId: (string | null)[];
  runFontByStyleId: Record<string, ResolvedRunFont>;
  runFontDocDefault: ResolvedRunFont;
}

// mammoth's own default image converter (mammoth.images.dataUri) only ever sets `src`
// (and `alt`, if present) on the resulting <img> - it never carries over the DOCX's own
// display size, so a browser falls back to the image FILE's intrinsic pixel size, which
// is very often much larger than what Word actually rendered (Word scales images down
// via size metadata alone, without re-encoding the file). This wraps mammoth's own
// documented image-conversion helper (mammoth.images.imgElement) to additionally apply
// the exact size resolveImageDimensions (docx-xml-model.ts) found for that image, so the
// <img> keeps the source's aspect ratio and scale while never overflowing its container.
export function createImageConverter(mammothImages: { imgElement: (fn: (image: any) => any) => any }, imageDimensions: Array<ImageDimension | null>) {
  let index = -1;
  return mammothImages.imgElement((image: { readAsBase64String: () => Promise<string>; contentType: string }) => {
    const dimension = imageDimensions[++index] || null;
    return image.readAsBase64String().then((imageBuffer: string) => {
      const result: { src: string; style?: string } = {
        src: `data:${image.contentType};base64,${imageBuffer}`,
      };
      if (dimension) {
        result.style = `width: ${dimension.widthPt}pt; max-width: 100%; height: auto; aspect-ratio: ${dimension.widthPt} / ${dimension.heightPt}; object-fit: contain;`;
      }
      return result;
    });
  });
}

export async function prepareConversionContext(arrayBuffer: ArrayBuffer): Promise<ConversionContext> {
  const xmlParts = await readDocxXmlParts(arrayBuffer);
  const xmlModel = parseDocxXmlModel(xmlParts.documentXml);
  const dominantFormatsByLevel = resolveDominantFormatsPerLevel(xmlParts.numberingXml, xmlModel.paragraphNumbering);
  const runFormattingByStyleId = resolveRunFormattingByStyleId(xmlParts.stylesXml);
  const { byStyleId: runFontByStyleId, docDefault: runFontDocDefault } = resolveRunFontByStyleId(xmlParts.stylesXml);
  const levelDetails = resolveNumberingLevelDetails(xmlParts.numberingXml);
  const paragraphNumberLabel = computeParagraphNumberLabels(xmlModel.paragraphNumbering, levelDetails);

  // Build indent lookup from resolved level details, then resolve it per-paragraph
  // by index here (using OUR OWN parsed numId/ilvl). This must happen here rather
  // than inside the fidelity transform: mammoth's own paragraph.numbering object
  // (see mammoth/lib/docx/numbering-xml.js) only carries {isOrdered, level,
  // paragraphStyleId} - it has no numId field to key off, so any attempt to
  // recover numId from mammoth's model downstream silently fails.
  const numberingIndentByNumIdIlvl: Record<string, { leftTwips: number; hangingTwips: number }> = {};
  Object.entries(levelDetails).forEach(([key, detail]) => {
    numberingIndentByNumIdIlvl[key] = {
      leftTwips: detail.indentLeftTwips,
      hangingTwips: detail.hangingTwips,
    };
  });
  const paragraphNumberIndent: Array<{ leftTwips: number; hangingTwips: number } | null> = xmlModel.paragraphNumbering.map(
    (ref, i) => {
      if (!ref || paragraphNumberLabel[i] === null || paragraphNumberLabel[i] === undefined) {
        return null;
      }
      return numberingIndentByNumIdIlvl[`${ref.numId}:${ref.ilvl}`] || null;
    }
  );

  const explicitPageBreakCount = (xmlParts.documentXml.match(/<w:br[^>]*w:type="page"/g) || []).length;
  const lastRenderedPageBreakCount = xmlModel.runIsLastRenderedPageBreakMarker.filter(Boolean).length;
  const sectionPageBreakCount = xmlModel.paragraphHasSectionPageBreak.filter(Boolean).length;
  const numberedParagraphCount = xmlModel.paragraphNumbering.filter(Boolean).length;
  const computedLabelCount = paragraphNumberLabel.filter((l) => l !== null).length;
  const possibleSourceEncodingIssue = /â/.test(xmlParts.documentXml);
  const imageCount = xmlModel.imageDimensions.length;
  const imagesWithSizeCount = xmlModel.imageDimensions.filter((d) => d !== null).length;

  const diagnostics = [
    `[diagnostic] page-break signals found in source: ${explicitPageBreakCount} explicit (w:br type=page), ${lastRenderedPageBreakCount} Word-cached (w:lastRenderedPageBreak), ${sectionPageBreakCount} mid-document section break(s) (w:sectPr, excludes "continuous" sections and the mandatory final body-level section)`,
    `[diagnostic] ${numberedParagraphCount} numbered/bulleted paragraphs detected`,
    `[diagnostic] ${computedLabelCount} of ${numberedParagraphCount} numbered paragraphs rendered with exact computed labels (decimal/lowerLetter/upperLetter/lowerRoman/upperRoman); the rest use mammoth's default bullet/list rendering`,
    `[diagnostic] page geometry: ${Math.round(xmlModel.pageGeometry.pageWidthTwips / 56.7)}mm × ${Math.round(xmlModel.pageGeometry.pageHeightTwips / 56.7)}mm, margins top/bottom/left/right: ${Math.round(xmlModel.pageGeometry.marginTopTwips / 56.7)}/${Math.round(xmlModel.pageGeometry.marginBottomTwips / 56.7)}/${Math.round(xmlModel.pageGeometry.marginLeftTwips / 56.7)}/${Math.round(xmlModel.pageGeometry.marginRightTwips / 56.7)}mm`,
    ...(imageCount > 0
      ? [`[diagnostic] ${imageCount} image(s) detected, ${imagesWithSizeCount} with an exact source size applied (base64-embedded, aspect ratio preserved)`]
      : []),
    ...(possibleSourceEncodingIssue
      ? ['[diagnostic] possible pre-existing character-encoding issue detected in the source file itself (garbled "â" sequences already present in the raw XML before conversion) - check if smart quotes/em-dashes look correct when the original .docx is opened directly in Word']
      : []),
  ];

  return {
    styleMap: [
      ...DOCX_STYLE_MAP,
      LAST_RENDERED_PAGE_BREAK_STYLE_MAP_RULE,
      ...buildListFormatStyleMapOverrides(dominantFormatsByLevel),
    ],
    paragraphSpacing: xmlModel.paragraphSpacing,
    runFormattingByStyleId,
    runIsLastRenderedPageBreakMarker: xmlModel.runIsLastRenderedPageBreakMarker,
    paragraphHasSectionPageBreak: xmlModel.paragraphHasSectionPageBreak,
    tables: xmlModel.tables,
    listFormatCss: buildListFormatCss(dominantFormatsByLevel),
    diagnostics,
    pageGeometry: xmlModel.pageGeometry,
    explicitPageBreakCount,
    paragraphNumberLabel,
    paragraphNumberIndent,
    imageDimensions: xmlModel.imageDimensions,
    runParagraphStyleId: xmlModel.runParagraphStyleId,
    runFontByStyleId,
    runFontDocDefault,
  };
}

export async function runMammothConversion(
  arrayBuffer: ArrayBuffer,
  mammoth: any,
  context: Pick<
    ConversionContext,
    | 'styleMap'
    | 'paragraphSpacing'
    | 'runFormattingByStyleId'
    | 'runIsLastRenderedPageBreakMarker'
    | 'paragraphHasSectionPageBreak'
    | 'paragraphNumberLabel'
    | 'paragraphNumberIndent'
    | 'imageDimensions'
    | 'runParagraphStyleId'
    | 'runFontByStyleId'
    | 'runFontDocDefault'
  >
): Promise<{ html: string; warnings: string[] }> {
  const result = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      styleMap: context.styleMap,
      convertImage: createImageConverter(mammoth.images, context.imageDimensions),
      transformDocument: createFidelityTransformDocument(
        mammoth.transforms,
        context.styleMap,
        context.paragraphSpacing,
        context.runFormattingByStyleId,
        context.runIsLastRenderedPageBreakMarker,
        context.paragraphHasSectionPageBreak,
        context.paragraphNumberLabel,
        context.paragraphNumberIndent,
        context.runParagraphStyleId,
        context.runFontByStyleId,
        context.runFontDocDefault
      ),
    }
  );

  return {
    html: result.value,
    warnings: result.messages.map((message: { message: string }) => message.message),
  };
}

export function finalizeConversionHtml(rawHtml: string, tables: TableFidelity[]): string {
  return applyTableFidelity(rawHtml, tables);
}
